import React, { useRef } from "react";
import { Button } from "components/Button";
import { iconFileUpload, iconTrash, iconDownload } from "assets/icons";
import { 
  Container, 
  FileList, 
  FileItem, 
  FileName, 
  FileSize, 
  DeleteButton, 
  HiddenInput, 
  UploadArea, 
  ErrorText,
  FilePreview,
  DocumentIcon 
} from "./styles";

export interface UploadedFile {
  id: string;
  file: File | null; // null for existing files
  name: string;
  size: string;
  previewUrl?: string; // for image preview
}

export interface InputMultipleFilesProps {
  files: UploadedFile[];
  onAddFiles: (files: File[]) => void;
  onRemoveFile: (fileId: string) => void;
  error?: boolean;
  errorMessage?: string;
  accept?: string;
  maxFiles?: number;
}

export const InputMultipleFiles: React.FC<InputMultipleFilesProps> = ({
  files,
  onAddFiles,
  onRemoveFile,
  error,
  errorMessage,
  accept = "*/*",
  maxFiles = 10
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    
    if (selectedFiles.length > 0) {
      const remainingSlots = maxFiles - files.length;
      const filesToAdd = selectedFiles.slice(0, remainingSlots);
      onAddFiles(filesToAdd);
    }

    // Reset input to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getFileExtension = (filename: string): string => {
    return filename.split('.').pop()?.toUpperCase() || '';
  };

  const isImageFile = (filename: string): boolean => {
    const imageExtensions = ['JPG', 'JPEG', 'PNG', 'GIF', 'BMP', 'WEBP', 'SVG'];
    const extension = getFileExtension(filename).toUpperCase();
    return imageExtensions.includes(extension);
  };

  const canAddMore = files.length < maxFiles;

  return (
    <Container>
      <HiddenInput
        ref={fileInputRef}
        type="file"
        multiple
        accept={accept}
        onChange={handleFileChange}
      />
      
      <UploadArea>
        <Button
          label={`Selecionar Arquivos (${files.length}/${maxFiles})`}
          typeBtn="outline"
          size="large"
          type="button"
          icon={iconFileUpload}
          onClick={handleButtonClick}
          disabled={!canAddMore}
        />
        {!canAddMore && (
          <span style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.5rem' }}>
            Limite máximo de arquivos atingido
          </span>
        )}
      </UploadArea>

      {files.length > 0 && (
        <FileList>
          {files.map((uploadedFile) => (
            <FileItem key={uploadedFile.id}>
              <FilePreview>
                {isImageFile(uploadedFile.name) && uploadedFile.previewUrl ? (
                  <img 
                    src={uploadedFile.previewUrl} 
                    alt={uploadedFile.name}
                    style={{ 
                      width: '48px', 
                      height: '48px', 
                      objectFit: 'cover', 
                      borderRadius: '4px' 
                    }}
                  />
                ) : (
                  <DocumentIcon>
                    {iconDownload}
                  </DocumentIcon>
                )}
              </FilePreview>
              <div style={{ flex: 1 }}>
                <FileName>
                  {uploadedFile.name}
                  <span className="extension">
                    .{getFileExtension(uploadedFile.name)}
                  </span>
                </FileName>
                <FileSize>{uploadedFile.size}</FileSize>
              </div>
              <DeleteButton
                type="button"
                onClick={() => onRemoveFile(uploadedFile.id)}
                title="Remover arquivo"
              >
                {iconTrash}
              </DeleteButton>
            </FileItem>
          ))}
        </FileList>
      )}
      
      {error && errorMessage && <ErrorText>{errorMessage}</ErrorText>}
    </Container>
  );
}; 